import React, { useMemo } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { CategoryData, getExportingOptions } from '../utils/jsonDataProcessing';

interface MultiCategoryChartProps {
  categoryData: CategoryData;
}

const MultiCategoryChart: React.FC<MultiCategoryChartProps> = ({ categoryData }) => {
  const multiCategoryData = categoryData.multiCategoryCount
  .filter((count, index) => count > 0)
  .map((count, index) => ({
    name: index === 1 ? '1 Category' : `${index} Categories`,
    value: count
  }));

  // Calculate total keywords and average categories per keyword
  const totalKeywords = useMemo(() => 
    multiCategoryData.reduce((sum, item) => sum + item.value, 0), 
    [multiCategoryData]
  );
  
  const avgCategoriesPerKeyword = useMemo(() => {
    const weightedSum = multiCategoryData.reduce(
      (sum, item, index) => sum + (index === 0 ? 0 : item.value * (item.name === '1 Category' ? 1 : parseInt(item.name))), 
      0
    );
    return totalKeywords > 0 ? (weightedSum / totalKeywords).toFixed(2) : '0';
  }, [multiCategoryData, totalKeywords]);

  const options = {
    chart: {
      type: 'column',
      height: '400px'
    },
    title: {
      text: 'Keywords by Category Count'
    },
    subtitle: {
      text: `Average categories per keyword: ${avgCategoriesPerKeyword}`
    },
    xAxis: {
      categories: multiCategoryData.map(d => d.name),
      title: {
        text: 'Number of Categories per Keyword'
      }
    },
    yAxis: {
      min: 0,
      title: {
        text: 'Number of Keywords'
      }
    },
    legend: {
      enabled: false
    },
    tooltip: {
      formatter: function(this: Highcharts.TooltipFormatterContextObject): string {
        const percentage: string = ((this.y as number / totalKeywords) * 100).toFixed(1);
        return `<b>${this.x}</b><br/>
                Keywords: <b>${this.y}</b><br/>
                Percentage: <b>${percentage}%</b>`;
      }
    },
    plotOptions: {
      column: {
        colorByPoint: true,
        colors: ['#7cb5ec', '#90ed7d', '#f7a35c', '#8085e9', '#f15c80', '#e4d354', '#2b908f', '#f45b5b', '#91e8e1'],
        borderRadius: 3
      }
    },
    series: [{
      name: 'Keywords',
      data: multiCategoryData.map(d => d.value),
      dataLabels: {
        enabled: true,
        format: '{point.y} ({point.percentage:.1f}%)',
        formatter: function(this: Highcharts.PointLabelObject): string {
          const percentage: string = ((this.y as number / totalKeywords) * 100).toFixed(1);
          return `${this.y} (${percentage}%)`;
        }
      }
    }],
    ...getExportingOptions('Multi-Category Analysis')
  };

  return <HighchartsReact highcharts={Highcharts} options={options} />;
};

export default MultiCategoryChart;
